import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { openLoginViaQrMobileScanner } from 'actions/auth';
import { isAuthenticated } from 'actions/user';
import { TAB_CHANGE } from 'actionTypes/appDrawer';
import { postMessageToMobileApp } from 'components/app/helpers';
import ErrorBoundary from 'components/common/NullErrorBoundary';
import { ThreeDots } from 'components/customicons';
import SearchBox from 'components/Header/partials/headerDropdown/SearchBox';
import { Highlighter } from 'components/highlighter';
import { tabNames } from 'components/MobileHolding/utils';
import Render from 'components/render';
import { RenderByExchange } from 'components/render/RenderByExchangeType';
import { IS_DEMO, IS_RN_WEBVIEW } from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS, STORAGE_KEYS } from 'constants/enums';
import { SHOW_SCANNER_FOR_LOGIN_VIA_QR } from 'constants/featureFlag';
import { IS_HYBRID_APP_SUPPORTED } from 'constants/rnApp';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { isNil } from 'helpers/ramda';
import { navigateTo } from 'helpers/url';
import {
  getContractType,
  isEmpty,
  isMarketsPage,
  isTradePage,
  noop,
} from 'helpers/utils';
import useLocalStorage from 'hooks/useLocalStorage';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import {
  DeltaIndiaLogoMobile,
  DeltaLogoMobile,
  Hamburger,
  Profile,
  ScannerIcon,
  WalletIcon,
} from 'styles/assets/icons';
import Box from 'UIKit/Box';

import { DeltaGlobalDemoLogo, DeltaIndiaDemoLogo } from 'components/Header/Demo/DeltaIndiaDemoLogo';
import BellAlertsIcon from './partials/BellAlerts';
import styles from './primaryHeaderMobile.module.scss';

const PrimaryHeaderMobile = () => {
  const { t } = useTranslation(['headerFooter']);
  const history = useHistory();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const selectedProduct = useAppSelector(selectedProductSelector);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention
  const [_, setActiveTab] = useLocalStorage(
    STORAGE_KEYS.MOBILE_HOLDING_ACTIVE_TAB,
    tabNames[0] as string
  );

  const onSearchChange = useCallback(value => {
    setSearchTerm(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const goToUniversalSearch = () => {
    let path = routes.universalSearchMobile;
    if (location.search) {
      path += location.search;
    }
    history.push(path, { prevPath: location.pathname });
  };

  const appDispatch = useAppDispatch();
  const isLoggedin = useAppSelector(state => isAuthenticated(state.user));

  const [placard, setPlacard] = useLocalStorage(
    STORAGE_KEYS.SCANTOLOGIN_PLACARD_MOBILE,
    true
  );

  const onSignupClick = () => {
    trackMixpanelEvent('Signup Page - Form Opened', {
      site_demarkation: 'Web App',
      // TODO: fix lint issues
      // eslint-disable-next-line no-nested-ternary
      page_name: isMarketsPage()
        ? 'All Markets Page'
        : isTradePage()
        ? 'Trade Page'
        : 'Others - Web App',
    });
    // trackMixpanelEvent('Signup - Header', {
    //   page_name: 'All Markets Page',
    // });
    if (IS_HYBRID_APP_SUPPORTED) {
      postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.SIGNUP_CLICKED);
    } else {
      history.push(routes.signup);
    }
  };
  const onLogInClick = () => {
    trackMixpanelEvent('Login - Header', {
      page_name: 'All Markets Page',
    });

    if (IS_HYBRID_APP_SUPPORTED) {
      postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGIN_CLICKED);
    } else {
      history.push(routes.login);
    }
  };

  const onLogoClick = () => {
    if (IS_RN_WEBVIEW) {
      history.push(routes.homepage.default);
    } else {
      navigateTo('/');
    }
  };

  const onScannerClick = () => {
    if (!IS_RN_WEBVIEW) {
      return;
    }
    appDispatch(openLoginViaQrMobileScanner({}));
  };

  const onWalletClick = () => {
    trackMixpanelEvent('Wallet Clicked - All Pages - Header');
    // [WEB-3532]: on clicking balance icon move to positions tab with wallet active
    if (isEmpty(selectedProduct) || isNil(selectedProduct)) {
      history.push(routes.futures.trade);
    } else {
      history.push(
        `${routes.default}${getContractType(selectedProduct?.contract_type)}/trade/${
          selectedProduct?.underlying_asset?.symbol
        }/${selectedProduct?.symbol}`
      );
    }
    setActiveTab(tabNames[0]);
    appDispatch({
      type: TAB_CHANGE,
      payload: APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
    });
  };

  const onAccountClick = () => {
    trackMixpanelEvent('Account icon clicked - All Pages - Header');
    history.push(routes.account.my_account);
  };

  const onMenuClick = () => {
    trackMixpanelEvent('Three dots clicked - All Pages - Header');
    history.push(routes.resources);
  };

  return (
    (<div
      className={styles['mobile-primary-header']}
      data-palette="PrimaryHeaderMobile">
      <span
        tabIndex={0}
        role="button"
        onKeyDown={noop}
        data-testid="logo-mobile"
        className={styles.logo}
        onClick={onLogoClick}
      >
        <RenderByExchange
          global={IS_DEMO? <DeltaGlobalDemoLogo /> : <DeltaLogoMobile />}
          indian={IS_DEMO? <DeltaIndiaDemoLogo /> : <DeltaIndiaLogoMobile />}
        />
      </span>
      <span className={styles.universalSearch}>
        <Box
          as="div"
          data-testid="goToUniversalSearchMobile-MarketsPage"
          onClick={goToUniversalSearch}
        >
          <SearchBox
            // @ts-ignore
            headerInputStyles={styles.searchInput}
            onSearchChange={onSearchChange}
            searchValue={searchTerm}
            clearSearch={clearSearch}
            autoFocus={false}
          />
        </Box>
      </span>
      {!isLoggedin ? (
        <div className={styles['sign-up']} data-palette="PrimaryHeaderMobile">
          <button
            type="button"
            data-testid="login-button"
            className={styles['login-button']}
            onClick={onLogInClick}
          >
            {t('headerFooter:header.logIn')}
          </button>
          <button
            type="button"
            data-testid="sign-up-button"
            className={styles['sign-up-button']}
            onClick={onSignupClick}
          >
            {t('headerFooter:header.signUp')}
          </button>
        </div>
      ) : (
        <>
          <Render when={SHOW_SCANNER_FOR_LOGIN_VIA_QR}>
            <Highlighter
              dataVersion={12}
              dataId=""
              title={t('headerFooter:header.scanToLoginPlacard')}
              isOpen={placard}
              className={styles.scanToLoginPlacard}
              toggleHighLigherAction={() => setPlacard(false)}
            />
            <div
              tabIndex={0}
              role="button"
              onKeyDown={noop}
              data-testid="scanner-menu"
              className={styles['scanner-menu']}
              onClick={onScannerClick}
            >
              <ScannerIcon fill="var(--headerIconColor)" />
            </div>
          </Render>

          {/* <RenderForGlobalExchange> */}
          <div
            tabIndex={0}
            role="button"
            onKeyDown={noop}
            data-testid="wallet-menu"
            className={styles['wallet-menu']}
            onClick={onWalletClick}
            data-palette="PrimaryHeaderMobile">
            <WalletIcon />
          </div>
          {/* </RenderForGlobalExchange> */}

          <div
            tabIndex={0}
            role="button"
            onKeyDown={noop}
            data-testid="profile-menu"
            className={styles['profile-menu']}
            onClick={onAccountClick}
            data-palette="PrimaryHeaderMobile">
            <Profile />
          </div>

          <ErrorBoundary>
            <BellAlertsIcon />
          </ErrorBoundary>
        </>
      )}
      <RenderByExchange
        global={
          !isLoggedin && (
            <div
              tabIndex={0}
              role="button"
              onKeyDown={noop}
              data-testid="side-menu"
              className={styles['side-menu']}
              onClick={onMenuClick}
              data-palette="PrimaryHeaderMobile">
              <ThreeDots />
            </div>
          )
        }
        indian={
          !isLoggedin && (
            <div
              tabIndex={0}
              role="button"
              onKeyDown={noop}
              data-testid="side-menu"
              className={styles['side-menu']}
              onClick={onMenuClick}
              data-palette="PrimaryHeaderMobile">
              <Hamburger width={18} height={18} />
            </div>
          )
        }
      />
    </div>)
  );
};

export default PrimaryHeaderMobile;
